import React, { useEffect, useMemo, useState, useLayoutEffect } from 'react';
import { Card, CardHeader, CardBody, Col } from 'reactstrap';
import AppLogo from './../../assets/img/brand/logo.jpeg';
import { Link } from 'react-router-dom';
import { Redirect, useLocation } from 'react-router';
import queryString from 'query-string';
import authService from '../../services/auth';
import { useQuery } from 'react-query';
import { queryKeys } from '../../config/queryKeys';
import { resolveInvitation } from '../../services/invitesClient';
import { useDispatch } from 'react-redux';
import { invitationActionTypes } from '@/store/Invitation/Invitation.actionTypes';

function ExpiredMessage({ isUserAuthorized }) {
  return (
    <>
      <small>Invitation Link is expired</small>
      <div className="text-center">
        <Link
          to="/auth/sign-up"
          className="mt-4 w-50 btn btn-default"
          color="default"
          type="button"
        >
          {isUserAuthorized ? 'Home' : 'Join now'}
        </Link>
      </div>
    </>
  );
}

// function readPendingInviteToken(invitationId) {
//   try {
//     const invitationData = useSelector(
//       (state) => state.invitation.invitationData
//     );
//     if (!invitationData) return null;
//     if (invitationData?.invitationId !== invitationId) return null;
//     return invitationData?.inviteToken || null;
//   } catch {
//     return null;
//   }
// }

export default function InvitationPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { i: invitationId, t: tokenFromQuery } = queryString.parse(
    location.search
  );

  const effectiveToken = useMemo(
    () => tokenFromQuery || null,
    [tokenFromQuery]
  );

  const {
    isFetching: isAuthFetching,
    isError: isAuthError,
    isSuccess: isAuthSuccess,
    isFetched: isAuthFetched,
    data: authUserData,
  } = useQuery(
    queryKeys.auth.currentAccount,
    async () => await authService.currentAccount()
  );

  const {
    isFetching: isInFetching,
    isError: isInError,
    isSuccess: isInSuccess,
    isFetched: isInFetched,
    error: InError,
    data: invitationData,
  } = useQuery(
    queryKeys.invitations.detail(
      invitationId,
      effectiveToken,
      authUserData?.email ?? ''
    ),
    async () => {
      const res = await resolveInvitation(
        invitationId,
        effectiveToken,
        authUserData?.email
      );
      if (!res.success) {
        throw new Error(res.message || 'Invitation not found.');
      }
      return res.data;
    },
    {
      enabled: !!invitationId && (!!effectiveToken || isAuthFetched),
    }
  );

  const tokenlessWaitingForSession =
    !!invitationId && !effectiveToken && !isAuthFetched;

  const inviteEmail =
    invitationData?.inviteeEmail || invitationData?.userDetails?.email;
  const isSameUser =
    authUserData?.email?.toLowerCase() === inviteEmail?.toLowerCase();
  const isNotSameUser =
    typeof authUserData?.email === 'string' &&
    typeof inviteEmail === 'string' &&
    authUserData?.email?.toLowerCase() !== inviteEmail?.toLowerCase();
  const isInviteInactive =
    invitationData?.accepted === true ||
    invitationData?.status === 'accepted' ||
    invitationData?.status === 'declined' ||
    invitationData?.status === 'expired' ||
    invitationData?.status === 'revoked';

  const isUserRegistered = invitationData?.userDetails?.registered ?? false;
  const isUserAuthorized = authUserData?.email ? true : false;

  const loginRedirectQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (invitationId) params.set('i', invitationId);
    if (effectiveToken) params.set('t', effectiveToken);
    const q = params.toString();
    return q ? `?${q}` : '';
  }, [invitationId, effectiveToken]);

  useEffect(() => {
    if (isInSuccess && isInFetched && invitationData && !isInviteInactive) {
      dispatch({
        type: invitationActionTypes.SET_INVITATION_DATA,
        payload: {
          ...invitationData,
          invitationId,
          inviteToken: effectiveToken || null,
        },
      });
    }
  }, [
    isInFetched,
    invitationData,
    isInSuccess,
    invitationId,
    effectiveToken,
    isInviteInactive,
  ]);

  if (!invitationId) {
    return <Redirect to="/" />;
  }
  if (isAuthFetched && isAuthSuccess && isInFetched && isInSuccess) {
    if (!isUserAuthorized) {
      if (isUserRegistered) {
        return <Redirect to={`/auth/login${loginRedirectQuery}`} />;
      } else {
        return <Redirect to={`/auth/sign-up${loginRedirectQuery}`} />;
      }
    }
    if (isUserAuthorized && !isInviteInactive && isSameUser) {
      return <Redirect to="/invitations-hub" />;
    }
  }

  const invitationLoading =
    isAuthFetching || tokenlessWaitingForSession || isInFetching;

  const getMessageToRender = () => {
    if (isUserAuthorized == false) {
      if (isInviteInactive) {
        return <ExpiredMessage isUserAuthorized={isUserAuthorized} />;
      } else {
        return (
          <p className="font-weight-bold mt-1">
            Please <Link to="/auth/login"> login</Link> or{' '}
            <Link to="/auth/sign-up"> sign up</Link> to accept this invitation
          </p>
        );
      }
    } else if (isInviteInactive) {
      return <ExpiredMessage isUserAuthorized={isUserAuthorized} />;
    } else if (isNotSameUser) {
      return (
        <p className="font-weight-bold mt-1">
          You have an active session logged in. Please logout to accept this
          invitation
        </p>
      );
    } else {
      return (
        <p className="font-weight-bold mt-1">
          You have opened wrong invitation link. Please{' '}
          <Link to="/auth/login"> login</Link> to relevant account to accept
          this invitation.
        </p>
      );
    }
  };
  return (
    <>
      <Col lg="5" md="7">
        <Card className="bg-secondary shadow border-0">
          <CardHeader className="bg-transparent border-0">
            <div className="text-center">
              <img className="auth-app-logo" src={AppLogo} alt="workfromhome" />
            </div>
          </CardHeader>
          <CardBody className="px-lg-5 pb-lg-5 pt-lg-1">
            <div className="text-muted text-center mt-2">
              <h2>Invitation</h2>
              {invitationLoading ? (
                <p className="font-weight-bold mt-3 mb-0">Loading…</p>
              ) : null}
              {!invitationLoading && isInSuccess && isAuthSuccess && (
                <>{getMessageToRender()}</>
              )}
              {!invitationLoading && (isInError || isAuthError) && (
                <>
                  <p className="font-weight-bold text-danger mt-2">
                    {InError?.message ||
                      'We could not load this invitation. The link may be invalid or expired.'}
                  </p>
                  <ExpiredMessage isUserAuthorized={isUserAuthorized} />
                </>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </>
  );
}
